import { useEffect, useState } from 'react';
import { searchClient } from 'algolia/searchClient';
import { useLocalizedIndex } from 'providers/algolia/localized-index';

export type RecommendationModel =
  | 'bought-together'
  | 'related-products'
  | 'trending-items'
  | 'trending-facets'
  | 'looking-similar';

interface UseRecommendationProductsOptions {
  model: RecommendationModel;
  objectID?: string;
  threshold?: number;
}

export function useRecommendationProducts({ objectID, model, threshold = 1 }: UseRecommendationProductsOptions) {
  const { indexName } = useLocalizedIndex({ type: 'products' });
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | Error>(null);

  useEffect(() => {
    if (!model) return;
    let isMounted = true;
    setLoading(true);
    setError(null);
    const fetchRecommendations = async () => {
      try {
        const recommendClient = searchClient?.initRecommend();
        const requests = [
          {
            model,
            indexName,
            objectID,
            threshold,
            facetName: '_collections',
          },
        ];
        const results = await recommendClient.getRecommendations({
          requests: requests as [],
        });
        if (isMounted) {
          setItems(results?.results?.[0]?.hits || []);
        }
      } catch (e: any) {
        if (isMounted) {
          setItems([]);
          setError(e);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchRecommendations();
    return () => {
      isMounted = false;
    };
  }, [objectID, model, indexName, threshold]);

  return { items, loading, error };
}

export default useRecommendationProducts;
